import { Check, Close, HelpOutline, WarningAmber } from '@mui/icons-material';
import { Box, useTheme } from '@mui/material';
import { FC } from 'react';

import { CONFIDENCE_STATUS_COLORS } from 'utils/constants/confidenceStatusColors';
import { ConfidenceLevel, SectionStatus } from 'utils/enums/Confidence';

interface ConfidenceIconProps {
  level: ConfidenceLevel;
  sectionStatus?: SectionStatus;
}

const getIconContainerSx = (borderColor: string) => ({
  width: 20,
  height: 20,
  borderRadius: '50%',
  border: `1.5px solid ${borderColor}`,
  backgroundColor: 'common.white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const getInnerIconSx = (color: string) => ({
  color,
  fontSize: 14
});

export const ConfidenceIcon: FC<ConfidenceIconProps> = ({
  level,
  sectionStatus
}) => {
  const theme = useTheme();

  const isMissingData = sectionStatus === SectionStatus.ERROR;

  const renderStatusIcon = () => {
    if (isMissingData) {
      const { main } = CONFIDENCE_STATUS_COLORS.missingData;

      return (
        <Box data-testid="confidence-icon-low" sx={getIconContainerSx(main)}>
          <Close sx={getInnerIconSx(main)} />
        </Box>
      );
    }

    switch (level) {
      case ConfidenceLevel.HIGH: {
        return (
          <Box
            data-testid="confidence-icon-high"
            sx={getIconContainerSx(theme.palette.success.main)}
          >
            <Check sx={getInnerIconSx(theme.palette.success.main)} />
          </Box>
        );
      }
      case ConfidenceLevel.LOW: {
        const { main } = CONFIDENCE_STATUS_COLORS.lowConfidence;

        return (
          <Box data-testid="confidence-icon-low" sx={getIconContainerSx(main)}>
            <Close sx={getInnerIconSx(main)} />
          </Box>
        );
      }
      case ConfidenceLevel.MEDIUM: {
        return (
          <WarningAmber
            data-testid="confidence-icon-medium"
            sx={{ color: CONFIDENCE_STATUS_COLORS.needsReview.main }}
            fontSize="small"
          />
        );
      }
      default: {
        return (
          <HelpOutline data-testid="confidence-icon-unknown" fontSize="small" />
        );
      }
    }
  };

  return renderStatusIcon();
};
